/**
 * Поля формы «Проверка завершена»: получатели, копия, техредактор, тип проверки + кол-во правок, комментарий.
 */
import { useRef } from 'react';
import { UserRecipientCombo, type RecipientUserItem } from '@/components/UserRecipientCombo/UserRecipientCombo';
import { TecheditorCombo, type TecheditorValueItem } from '@/components/TecheditorCombo/TecheditorCombo';
import { SelectDropdown } from '@/components/ui/SelectDropdown/SelectDropdown';
import { Input } from '@/components/ui/Input/Input';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Switch } from '@/components/ui/Switch/Switch';
import {
  REVIEW_CHECK_TYPES,
  REVIEW_CHECK_TYPE_LABELS,
  REVIEW_CHECK_TYPE_PLACEHOLDER,
  REVIEW_NOT_SPECIFIED_LABEL,
} from '@/utils/reviewCheckType';
import type { ReviewCheckType } from '@/api/client';

export interface ProjectReviewedEmailFieldsProps {
  recipients: RecipientUserItem[];
  onRecipientsChange: (items: RecipientUserItem[]) => void;
  ccUsers: RecipientUserItem[];
  onCcUsersChange: (items: RecipientUserItem[]) => void;
  techeditors: TecheditorValueItem[];
  onTecheditorsChange: (items: TecheditorValueItem[]) => void;
  reviewType: string;
  onReviewTypeChange: (value: string) => void;
  correctionsCount: string;
  onCorrectionsCountChange: (value: string) => void;
  noEdits: boolean;
  onNoEditsChange: (value: boolean) => void;
  commentNote: string;
  onCommentNoteChange: (value: string) => void;
  disabled?: boolean;
}

export function ProjectReviewedEmailFields({
  recipients,
  onRecipientsChange,
  ccUsers,
  onCcUsersChange,
  techeditors,
  onTecheditorsChange,
  reviewType,
  onReviewTypeChange,
  correctionsCount,
  onCorrectionsCountChange,
  noEdits,
  onNoEditsChange,
  commentNote,
  onCommentNoteChange,
  disabled,
}: ProjectReviewedEmailFieldsProps) {
  const recipientRef = useRef<HTMLDivElement>(null);
  const ccComboRef = useRef<HTMLDivElement>(null);
  const techeditorRef = useRef<HTMLDivElement>(null);

  return (
    <div className="review-check-email-modal__fields">
      <div className="review-check-email-modal__field">
        <UserRecipientCombo
          value={recipients}
          onChange={onRecipientsChange}
          placeholder="Получатель"
          containerRef={recipientRef}
          disabled={disabled}
        />
      </div>
      <div className="review-check-email-modal__field">
        <UserRecipientCombo
          value={ccUsers}
          onChange={onCcUsersChange}
          placeholder="Копия"
          containerRef={ccComboRef}
          disabled={disabled}
        />
      </div>
      <div className="review-check-email-modal__field">
        <TecheditorCombo
          value={techeditors}
          onChange={onTecheditorsChange}
          placeholder="Техредактор"
          containerRef={techeditorRef}
          disabled={disabled}
        />
      </div>
      <div className="review-check-email-modal__row review-check-email-modal__row_cols-2-switch">
        <div className="review-check-email-modal__field">
          <SelectDropdown
            fullWidth
            value={reviewType}
            onChange={onReviewTypeChange}
            placeholder={REVIEW_CHECK_TYPE_PLACEHOLDER}
            options={[
              { value: '', label: REVIEW_NOT_SPECIFIED_LABEL },
              ...REVIEW_CHECK_TYPES.map((k) => ({ value: k, label: REVIEW_CHECK_TYPE_LABELS[k] })),
            ]}
            renderValue={() => {
              const k = reviewType.trim();
              if (!k) {
                return (
                  <span className="select-dropdown__value select-dropdown__placeholder">
                    {REVIEW_CHECK_TYPE_PLACEHOLDER}
                  </span>
                );
              }
              const lbl = REVIEW_CHECK_TYPE_LABELS[k as ReviewCheckType];
              return <span className="select-dropdown__value">{lbl ?? k}</span>;
            }}
            disabled={disabled}
          />
        </div>
        <div className="review-check-email-modal__field">
          <Input
            value={correctionsCount}
            onChange={(e) => onCorrectionsCountChange(e.target.value)}
            placeholder="Количество правок"
            fullWidth
            disabled={disabled || noEdits}
            inputMode="numeric"
          />
        </div>
        <div className="review-check-email-modal__field review-check-email-modal__field_switch">
          <Switch
            checked={noEdits}
            onCheckedChange={onNoEditsChange}
            disabled={disabled}
            label="Без правок"
          />
        </div>
      </div>
      <div className="review-check-email-modal__field">
        <Textarea
          value={commentNote}
          onChange={(e) => onCommentNoteChange(e.target.value)}
          placeholder="Комментарий"
          fullWidth
          minRows={1}
          maxRows={40}
          inputClassName="review-check-email-modal__comment-input"
          disabled={disabled}
        />
      </div>
    </div>
  );
}
